/**
 * Attachments (sak 54) — a file on a task or on a message.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT ATLE DECIDED, AND WHAT IS STILL OPEN.
 *
 * 30 August: build something quick and simple while this runs locally, fix it for production when
 * it is live; there must be a **maximum upload size** (the number not yet chosen) and there must be
 * **virus scanning**; the rest later.
 *
 * So this contract settles the two he named and deliberately leaves the rest unshaped: there is no
 * retention field, no expiry, no visibility flag and no versioning. Each of those is a decision, and
 * a schema that guessed at one would be harder to correct than an absent field.
 *
 * ---------------------------------------------------------------------------------------------
 * `MAX_ATTACHMENT_BYTES` IS 50 MB, AND THAT NUMBER IS MINE RATHER THAN HIS.
 *
 * Atle: "vi må ha en max opplastningsgrense (vet ikke hvor mye enda)". 50 MB clears the three things
 * this is actually for — a rider is under 2 MB, a signed contract a few, a press photo up to about
 * 40 — and stops short of video, which is a different product with a different bill. It is a
 * CONFIGURED ceiling on the API side, not a constant baked into a client, so changing it is an
 * environment variable rather than a release. This is the value the contract validates against so a
 * form can refuse before it uploads.
 *
 * ---------------------------------------------------------------------------------------------
 * `scanStatus` IS ON THE DTO BECAUSE THE UI HAS TO SAY WHY A FILE IS NOT DOWNLOADABLE YET.
 *
 * Four states, and `skipped` is not `clean`: it records that no scanner was configured when the file
 * arrived, which is a fact about the deployment rather than a claim about the file. A client shows
 * a pending file greyed with a word, an infected one refused, and a skipped one as ordinary or as
 * unverified depending on what the environment says — `downloadable` is the API's own answer to that
 * question, so no client has to reimplement the policy.
 */
import { z } from 'zod';
import { pageCursorFields, pageQuerySchema } from '../http/pagination.js';

import { attachmentOwnerSchema, scanStatusSchema } from '../enums.js';
import { advanceSectionKeySchema } from './booking-enquiries.js';

/**
 * The ceiling a client validates against. The API's own limit is configured and may be lower; a
 * client that refuses early is a courtesy, and the request is refused again either way.
 */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** What a filename may be. Long enough for a real one, short enough to render in a row. */
export const MAX_ATTACHMENT_FILENAME = 260;

export const attachmentDtoSchema = z
  .object({
    id: z.uuid(),
    ownerType: attachmentOwnerSchema,
    ownerId: z.uuid(),
    /** What the uploader called it. A label — never a path. */
    filename: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().positive(),
    /** SHA-256, hex. Shown nowhere; useful when somebody asks whether two files are the same. */
    checksum: z.string(),
    scanStatus: scanStatusSchema,
    scannedAt: z.iso.datetime().nullable(),
    /**
     * WHETHER THE API WILL ACTUALLY SERVE IT, answered by the server so no client reimplements the
     * rule. `pending` and `infected` are never downloadable; `skipped` depends on whether the
     * deployment requires a scanner, which is a fact only the server knows.
     */
    downloadable: z.boolean(),
    uploadedAt: z.iso.datetime(),
    /** Who put it there, resolved for display. Null when the contact card has been removed. */
    uploadedByName: z.string().nullable(),
    /**
     * HUSDOKUMENT: HVILKEN ADVANCE-SEKSJON DET FØLGER ENHVER ACT INN I (vedtak O-6, 0192).
     *
     * NULL betyr «hører til én artist eller én anledning», og da tilbys det ikke i advancens
     * plukker. Meningsfullt bare når eieren er en MAPPE — en fil på en oppgave eller en melding
     * har ingenting med en advance å gjøre, og feltet er null der.
     */
    advanceSection: advanceSectionKeySchema.nullable(),
    /**
     * TYPEN FILA ÅPNES SOM I EN NY FANE, ELLER NULL NÅR DEN BARE LASTES NED (sak 253.82).
     *
     * Bare PDF, bilder og ren tekst, og bare når filnavnet og typen nettleseren oppga er enige —
     * se `viewableType`. Word, HTML, SVG og zip lastes ned. Serveren svarer det, og
     * `GET /v1/attachments/:id/view` sender nøyaktig denne typen, aldri den lagrede.
     */
    viewableAs: z.string().nullable().optional(),
    /**
     * RIDEREN FOR SEG (QA13 75, 253.47, 0939). Bare en fil i en advance-seksjon kan være en rider,
     * og bare en rider kan være godkjent — når og av hvem. Valgfrie i kontrakten fordi testoppsett
     * bygger DTO-en for hånd; API-et sender dem alltid.
     */
    rider: z.boolean().optional(),
    riderApprovedAt: z.iso.datetime().nullable().optional(),
    riderApprovedByName: z.string().nullable().optional(),
  })
  .meta({ id: 'Attachment' });

/**
 * FILENE SOM KAN ÅPNES I NY FANE (sak 253.82): filendelsen, og typen som da sendes — fast, ikke
 * den lagrede. SVG er et bilde som kan kjøre skript, og står derfor ikke her.
 */
const VIEWABLE: Readonly<Record<string, { type: string; accepts: readonly string[] }>> = {
  pdf: { type: 'application/pdf', accepts: ['application/pdf'] },
  png: { type: 'image/png', accepts: ['image/png'] },
  jpg: { type: 'image/jpeg', accepts: ['image/jpeg', 'image/jpg', 'image/pjpeg'] },
  jpeg: { type: 'image/jpeg', accepts: ['image/jpeg', 'image/jpg', 'image/pjpeg'] },
  gif: { type: 'image/gif', accepts: ['image/gif'] },
  webp: { type: 'image/webp', accepts: ['image/webp'] },
  txt: { type: 'text/plain; charset=utf-8', accepts: ['text/plain'] },
};

/**
 * Typen fila kan vises som, eller null. Filendelsen OG den lagrede typen må peke på det samme: en
 * `rider.pdf` lastet opp som `text/html` er ikke en PDF man skal åpne, og en `side.html`
 * omdøpt til `side.txt` med `text/html` heller ikke.
 */
export function viewableType(filename: string, contentType: string): string | null {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return null;
  const entry = VIEWABLE[filename.slice(dot + 1).toLowerCase()];
  if (entry === undefined) return null;
  const base = (contentType.split(';')[0] ?? '').trim().toLowerCase();
  return entry.accepts.includes(base) ? entry.type : null;
}

/**
 * Å MERKE ET HUSDOKUMENT — ELLER Å TA MERKET BORT.
 *
 * Ett felt, fordi det er ett valg: «gjelder dette enhver act, og i så fall hvor?». `null` er svaret
 * for et dokument som hører til én artist eller én anledning, og det er standarden.
 */
export const updateAttachmentRequestSchema = z
  .object({ advanceSection: advanceSectionKeySchema.nullable() })
  .meta({ id: 'UpdateAttachmentRequest' });
export type UpdateAttachmentRequest = z.infer<typeof updateAttachmentRequestSchema>;

/**
 * RIDER, OG GODKJENT (QA13 75, 253.47). Det samme skjemaet for en fil og for en lenke i advancen.
 *
 * `rider: false` tar også godkjenningen bort. `approved` kan bare settes av arrangøren — huset som
 * eier programposten — og bare på en rider; byrået legger rideren inn, festivalen godkjenner den.
 */
export const markRiderRequestSchema = z
  .object({ rider: z.boolean().optional(), approved: z.boolean().optional() })
  .refine((body) => body.rider !== undefined || body.approved !== undefined, {
    message: 'rider or approved',
  })
  .meta({ id: 'MarkRiderRequest' });
export type MarkRiderRequest = z.infer<typeof markRiderRequestSchema>;

export const listAttachmentsQuerySchema = z
  .object({
    /** Whose files. Both required: an owner id with no type is an id compared across two tables. */
    ownerType: attachmentOwnerSchema,
    ownerId: z.uuid(),
    /* §8.3: markør, med listas gamle tak som standard — se `pageQuerySchema`. */
    ...pageQuerySchema(500).shape,
  })
  .meta({ id: 'ListAttachmentsQuery' });

/**
 * EN SIDE, MED HELE LISTA SOM STANDARD (AI-ANALYSE §8.3, 26.10).
 *
 * Fram til 26.10 var dette ikke en side: «filene på ÉN oppgave eller ÉN melding». Men den samme ruta
 * gir filene i en dokumentmappe, og den kan vokse. Nå har den `limit`/`cursor` og `nextCursor` som
 * alt annet; standarden (500) er større enn noen mappe i dag, så nettsiden som ikke sender `limit`
 * får hele lista som før, i id-orden.
 */
export const attachmentListResponseSchema = z
  .object({ items: z.array(attachmentDtoSchema), ...pageCursorFields })
  .meta({ id: 'AttachmentListResponse' });

export type AttachmentDto = z.infer<typeof attachmentDtoSchema>;
export type ListAttachmentsQuery = z.infer<typeof listAttachmentsQuerySchema>;
export type AttachmentListResponse = z.infer<typeof attachmentListResponseSchema>;
